import { useState, useEffect, useCallback, useRef } from "react";

export type SceneType = "upload" | "leads" | "funnel" | "metrics" | "abtest";

const SCENES: SceneType[] = ["upload", "leads", "funnel", "metrics", "abtest"];
const SCENE_DURATIONS: Record<SceneType, number> = {
  upload: 2500,
  leads: 3500,
  funnel: 5000,
  metrics: 3500,
  abtest: 2500,
};

interface UseSceneAutoplayOptions {
  pauseOnHover?: boolean;
}

export function useSceneAutoplay(options: UseSceneAutoplayOptions = {}) {
  const { pauseOnHover = true } = options;

  const [currentScene, setCurrentScene] = useState<SceneType>("upload");
  const [isHovering, setIsHovering] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const sceneTimeout = useRef<NodeJS.Timeout | null>(null);

  const currentIndex = SCENES.indexOf(currentScene);
  const progress = ((currentIndex + 1) / SCENES.length) * 100;

  const goToScene = useCallback((scene: SceneType) => {
    // Limpa timeout anterior
    if (sceneTimeout.current) {
      clearTimeout(sceneTimeout.current);
    }
    setCurrentScene(scene);
    setManualOverride(true); // Força autoplay a continuar
  }, []);

  const nextScene = useCallback(() => {
    setCurrentScene((prev) => {
      const currentIdx = SCENES.indexOf(prev);
      const nextIdx = (currentIdx + 1) % SCENES.length;
      return SCENES[nextIdx];
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) {
      setIsHovering(true);
    }
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) {
      setIsHovering(false);
      setManualOverride(false); // Reset ao sair
    }
  }, [pauseOnHover]);

  // Autoplay logic
  useEffect(() => {
    // Só pausa com hover SE não tiver clique manual recente
    if (isHovering && !manualOverride) {
      if (sceneTimeout.current) {
        clearTimeout(sceneTimeout.current);
      }
      return;
    }

    const duration = SCENE_DURATIONS[currentScene];
    sceneTimeout.current = setTimeout(() => {
      nextScene();
      setManualOverride(false); // Reset após transição
    }, duration);

    return () => {
      if (sceneTimeout.current) {
        clearTimeout(sceneTimeout.current);
      }
    };
  }, [currentScene, isHovering, manualOverride, nextScene]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sceneTimeout.current) {
        clearTimeout(sceneTimeout.current);
      }
    };
  }, []);

  return {
    currentScene,
    scenes: SCENES,
    progress,
    isPaused: isHovering && !manualOverride,
    goToScene,
    handleMouseEnter,
    handleMouseLeave,
  };
}
